import {Component, Input, NgZone, OnInit, Renderer2} from "@angular/core";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {CssSelection} from "../content/content-view.objects";
import {ComponentBase} from "../base/ComponentBase";
import {WordWallRecordingService, WordWallItem} from "../services/recording/wordwall.recording.service";
import {RaVersion} from "../services/configuration/ra.version";
import {AudioQueueService} from "../services/audio-playback/audioqueue.service";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";

interface ITableFormat {
    ngForIndices: number[];
    class: string;
}

@Component({
    providers: [],
    selector: "wordwall-view",
    templateUrl: "wordwall-view.component.html",
    styleUrls:["wordwall-view.component.scss"]
})

export class WordWallViewComponent extends ComponentBase implements OnInit {

    @Input()
    set content(value: CssSelection) {
        this._content = value;
    }

    get content(): CssSelection {
        return this._content;
    }

    private _content: CssSelection = new CssSelection();
    public volumeProgress = 0;
    public volumeColor = "";
    public wordWallTableFormats: ITableFormat[];
    public static NUMTABLES = 2;
    public static MAXITEMS = 15;
    public volumeBar : { volume: boolean }[] = Array.from({ length: 9 } , () => ({ volume: false }));

    constructor(public wordWallRecordingService: WordWallRecordingService,
                private _ngZone: NgZone,
                private renderer: Renderer2,
                private audioQueueService: AudioQueueService,
                private log: BugsnagLoggingService) {
        super("WordWallViewComponent");
    }

    public ngOnInit(): void {
        this.log.info(`Init component (override): ${this.componentName}  ${this.uuid}`);
        this.wordWallRecordingService.setVolumeCallback((result) => {
            this.getVolumeProgress(result);
        });
        this.wordWallTableFormats =
            this.getTableFormats(WordWallViewComponent.MAXITEMS, WordWallViewComponent.NUMTABLES);
        this.setButtonBlinkingListener();
    }


    public ngOnDestroy(): void {
        this.log.info(`Destroy component (override): ${this.componentName}  ${this.uuid}`);
        this.wordWallRecordingService.deactivate();
        super.ngOnDestroy();
    }

    private getTableFormats(maxItems: number, numTables: number): ITableFormat[] {
        let tableFormats:ITableFormat[] = [];
        let tableFormat:ITableFormat = {ngForIndices: [], class: ""};
        let tableRows:number = Math.ceil(maxItems / numTables);
        this.log.info("tableRows = " + tableRows);
        let bootStrapMaxGrids = 12;
        for (let i = 0; i < numTables; i++) {
            // new way numbers items across rather than down.
            tableFormat.ngForIndices = Array.from(Array(tableRows), (x, j) => ((j * numTables) + i));
            let numGrids = Math.floor(bootStrapMaxGrids / numTables);
            tableFormat.class = "table-responsive col-md-" + numGrids.toString();
            tableFormats.push(JSON.parse(JSON.stringify(tableFormat)));
        }
        return tableFormats;
    }

    public getFontSize(itemIndex: number): string {
        let size = "18pt";
        if (itemIndex < this.wordWallRecordingService.wordWallItems.length) {
            let itemLength = this.wordWallRecordingService.wordWallItems[itemIndex].text.length;
            if (itemLength > 24) {
                size = "16pt";
            }
        }
        return size;
    }
    public getBalloonFontSize(item: WordWallItem): string {
        let size = "20pt";
        // scale font size for long glossary strings
        if (item) {
            if (item.text.length > 30) {
                // glossary item that motivated this this is "sputtered and puffed and squeaked" - 33 chars
                // selection: Hal and Al
                size = "13pt";
            } else if (item.text.length > 26) {
                // glossary item that motivated this this is "last full measure of devotion" - 29 chars
                // selection: The Gettysburg Address
                size = "16pt";
            } else if (item.text.length > 23) {
                // there are a number of glossary entries that require this rule
                size = "18pt";
            }
        }
        return size;
    }

    public getText(itemIndex:number): string {
        let text = "";
        if (itemIndex < this.wordWallRecordingService.wordWallItems.length) {
            if (this.wordWallRecordingService.wordWallItems[itemIndex].isComplete ||
            !this.wordWallRecordingService.isGuided) {
                text = this.wordWallRecordingService.wordWallItems[itemIndex].text;
            }
        }
        return text;
    }

    public itemIsComplete(itemIndex:number): boolean {
        let itemIsComplete = false;
        if (itemIndex < this.wordWallRecordingService.wordWallItems.length) {
            itemIsComplete = this.wordWallRecordingService.wordWallItems[itemIndex].isComplete;
        }
        return itemIsComplete;
    }

    public glossary(itemIndex: number): void {
        this.wordWallRecordingService.currentItem = this.wordWallRecordingService.wordWallItems[itemIndex];
        let hideCloseButton = false;
        let visualOnly = false;
        this.wordWallRecordingService.launchGlossary(hideCloseButton, visualOnly);
    }

    public record(itemIndex: number): void {
        this.audioQueueService.reset().subscribe(() => {
            let hideCloseButton = false;
            let visualOnly = true;

            this.wordWallRecordingService.currentItem = this.wordWallRecordingService.wordWallItems[itemIndex];
            // if we are pressing the record button, display the correct glossary too, but no audio
            this.wordWallRecordingService.launchGlossary(hideCloseButton, visualOnly);
            this.wordWallRecordingService.playRepeatAfterMeAudio();
        });
    }

    public finishWordWall(): void {
        this.wordWallRecordingService.finishWordWall();
    }

    private setButtonBlinkingListener(): void {
        this.renderer.listen("document", "click", (event) => {
            if (this.wordWallRecordingService.isAnimationActive) {
                this.wordWallRecordingService.cancelTransition();
            }
        });
    }

    public get showButton(): boolean {
        let show = false;
        if (!this.wordWallRecordingService.isAnimationActive) {
            if (this.wordWallRecordingService.alwaysAllowStart || this.wordWallRecordingService.practiceComplete()) {
                show = true;
            }
        }
        return show;
    }

    public get showBlinkingButton(): boolean {
        return this.wordWallRecordingService.isAnimationActive;
    }

    public get clickDisabled(): boolean {
        return this.wordWallRecordingService.isWordInWordWallSelected();
    }

    public get isRecording(): boolean {
        return this.wordWallRecordingService.isRecording;
    }

    private getVolumeProgress(result): void {
        let MAX_AMPLITUDE = 32767;

        this._ngZone.run(() => {

            // get percentage of the MAX_AMPLITUDE that the user's voice hit
            let amplitude_ratio = (result * 1.0) / MAX_AMPLITUDE;
            let volumeBarRatio = Math.round(this.volumeBar.length * amplitude_ratio);
            this.volumeBar.forEach((obj,index) => {
                 obj.volume = (index + 1) <= volumeBarRatio ? true : false;
           });
        });
        
    }
}
